// Custom hooks for managing approval requests
// Provides data fetching and state management for approval requests using SWR

import { 
  useFlexibleList, 
  useEntity, 
  useCreateEntity, 
  useUpdateEntity, 
  useDeleteEntity 
} from '../../../hooks/useSWR';
import useSWRMutation from 'swr/mutation';
import { useAuth } from '../../../contexts/AuthContext';
import { API_BASE_URL } from '../../../config/apiConfig';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const savedUser = localStorage.getItem('user');
  let userId = null;
  
  if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
    try {
      const parsedUser = JSON.parse(savedUser);
      userId = parsedUser.id?.toString() || null;
    } catch (parseError) {
      userId = null;
    }
  }
  
  const headers = {
    'Content-Type': 'application/json',
    ...(userId && { 'user-id': userId })
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  return headers;
};

// ============================================================================
// DATA FETCHING HOOKS - Using SWR for caching and revalidation
// ============================================================================

/**
 * Hook to fetch all approval requests with optional filters
 * @param {object} filters - Query parameters for filtering
 * @returns {object} { data, isLoading, isError, mutate }
 */
export const useApprovalRequests = (filters = {}) => {
  const { data, isLoading, isError, mutate } = useFlexibleList('/approval-requests', filters);

  return {
    approvals: data || [],
    isLoading,
    isError,
    mutate
  };
};

/**
 * Hook to fetch approvals assigned to current user
 * @returns {object} { data, isLoading, isError, mutate }
 */
export const useMyApprovals = () => {
  const { data, isLoading, isError, mutate } = useFlexibleList('/approval-requests/my-approvals');

  return {
    approvals: data || [],
    isLoading,
    isError,
    mutate
  };
};

/**
 * Hook to fetch approval requests created by current user
 * @returns {object} { data, isLoading, isError, mutate }
 */
export const useMyRequests = () => {
  const { data, isLoading, isError, mutate } = useFlexibleList('/approval-requests/my-requests');

  return {
    requests: data || [],
    isLoading,
    isError,
    mutate
  };
};

/**
 * Hook to fetch a single approval request by ID
 * @param {number|string} id - Approval request ID
 * @returns {object} { data, isLoading, isError, mutate }
 */
export const useApprovalRequest = (id) => {
  const { data, isLoading, isError, mutate } = useEntity('/approval-requests', id);

  return {
    approval: data,
    isLoading,
    isError,
    mutate
  };
};

/**
 * Hook to fetch overdue approvals
 * @returns {object} { data, isLoading, isError, mutate }
 */
export const useOverdueApprovals = () => {
  const { data, isLoading, isError, mutate } = useFlexibleList('/approval-requests/overdue');

  return {
    approvals: data || [],
    isLoading,
    isError,
    mutate
  };
};

/**
 * Hook to fetch approval analytics and dashboard data
 * @returns {object} { data, isLoading, isError, mutate }
 */
export const useApprovalAnalytics = () => {
  const { data, isLoading, isError, mutate } = useFlexibleList('/approval-dashboard/analytics');

  return {
    analytics: data || {},
    isLoading,
    isError,
    mutate
  };
};

// ============================================================================
// MUTATION HOOKS - Using SWR mutations for data modification
// ============================================================================

/**
 * Hook to create approval requests with retry logic for unique constraint violations
 * @returns {function} createApprovalRequest function
 */
export const useCreateApprovalRequest = () => {
  const createEntity = useCreateEntity('/approval-requests');
  
  const createApprovalRequestWithRetry = async (data, fieldsToRemove = [], maxRetries = 3) => {
    // Helper function to generate approval code
    const generateApprovalCode = (workflowType = 'approval') => {
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      // Determine prefix based on workflow type
      let prefix;
      switch (workflowType) {
        case 'review':
          prefix = 'REV';
          break;
        case 'review_approval':
          prefix = 'AR';
          break;
        case 'approval':
        default:
          prefix = 'APR';
          break;
      }
      
      // Generate auto-increment number using timestamp for uniqueness
      const autoIncrement = Date.now().toString().slice(-6); // Last 6 digits of timestamp
      
      return `${prefix}-${dateStr}-${autoIncrement}`;
    };

    for (let i = 0; i < maxRetries; i++) {
      // Generate new code for each attempt
      const generatedCode = generateApprovalCode(data.workflowType || 'approval');
      const requestData = {
        ...data,
        code: generatedCode
      };
      
      try {
        console.log(`=== ATTEMPT ${i + 1} ===`);
        console.log(`Generated Code: ${generatedCode}`);
        console.log(`Sending to database:`, requestData);
        console.log(`========================`);
        
        const result = await createEntity(requestData, fieldsToRemove);
        console.log('✅ Approval request created successfully:', result);
        return result;
      } catch (error) {
        console.error(`Attempt ${i + 1} failed:`, error);
        console.log('Error details:', {
          status: error.status,
          statusCode: error.statusCode,
          errorCode: error.errorCode,
          message: error.message,
          maxRetries,
          currentAttempt: i + 1
        });
        
        // If it's a unique constraint violation and we have retries left, try again
        if ((error.status === 409 || error.statusCode === 409) && error.errorCode === 'P2002' && i < maxRetries - 1) {
          console.log(`❌ Unique constraint violation detected!`);
          console.log(`🔄 Retrying with new code... (attempt ${i + 2}/${maxRetries})`);
          console.log(`Previous code was: ${generatedCode}`);
          // Generate new code for next attempt
          const newCode = generateApprovalCode(data.workflowType || 'approval');
          console.log(`🆕 Generated new code: ${newCode}`);
          data = {
            ...data,
            code: newCode
          };
          continue;
        }
        
        // If it's not a unique constraint violation or we're out of retries, throw the error
        console.log('Not retrying - either not a unique constraint violation or out of retries');
        throw error;
      }
    }
  };
  
  return createApprovalRequestWithRetry;
};

/**
 * Hook to update approval requests
 * @returns {function} updateApprovalRequest function
 */
export const useUpdateApprovalRequest = () => {
  return useUpdateEntity('/approval-requests');
};

/**
 * Hook to delete approval requests
 * @returns {function} deleteApprovalRequest function
 */
export const useDeleteApprovalRequest = () => {
  return useDeleteEntity('/approval-requests');
};

/**
 * Hook to cancel approval requests
 * @returns {function} cancelApprovalRequest function
 */
export const useCancelApprovalRequest = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('authToken');
  
  const { trigger } = useSWRMutation('/approval-requests', async (url, { arg }) => {
    const { approvalId, comments } = arg;
    
    const headers = {
      'Content-Type': 'application/json',
      ...(user?.id && { 'user-id': user.id.toString() }),
    };
    
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}${url}/${approvalId}/cancel`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ 
        action: 'cancel',
        comments: comments || 'Approval request cancelled' 
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to cancel approval request' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  });

  return (approvalId, comments) => trigger({ approvalId, comments });
};

// ============================================================================
// APPROVAL ACTION HOOKS - Using SWR mutations for approval actions
// ============================================================================

/**
 * Hook for approval actions (approve, reject, delegate, escalate)
 * @returns {object} { approve, reject, delegate, escalate, isLoading, error }
 */
export const useApprovalActions = () => {
  const { user } = useAuth(); // Get user from auth context
  
  const { trigger: approveAction, isMutating: isApproving } = useSWRMutation(
    '/approval-requests',
    async (url, { arg }) => {
      const response = await fetch(`${API_BASE_URL}${url}/${arg.approvalId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.id && { 'user-id': user.id.toString() }) // Only include if user ID exists
        },
        body: JSON.stringify({
          action: 'approve',
          ...arg.data
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response.json();
    }
  );

  const { trigger: rejectAction, isMutating: isRejecting } = useSWRMutation(
    '/approval-requests',
    async (url, { arg }) => {
      const response = await fetch(`${API_BASE_URL}${url}/${arg.approvalId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.id && { 'user-id': user.id.toString() }) // Only include if user ID exists
        },
        body: JSON.stringify({
          action: 'reject',
          ...arg.data
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response.json();
    }
  );

  const { trigger: delegateAction, isMutating: isDelegating } = useSWRMutation(
    '/approval-requests',
    async (url, { arg }) => {
      const response = await fetch(`${API_BASE_URL}${url}/${arg.approvalId}/delegate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.id && { 'user-id': user.id.toString() }) // Only include if user ID exists
        },
        body: JSON.stringify({
          action: 'delegate',
          ...arg.data
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response.json();
    }
  );

  const { trigger: escalateAction, isMutating: isEscalating } = useSWRMutation(
    '/approval-requests',
    async (url, { arg }) => {
      const response = await fetch(`${API_BASE_URL}${url}/${arg.approvalId}/escalate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.id && { 'user-id': user.id.toString() }) // Only include if user ID exists
        },
        body: JSON.stringify({
          action: 'escalate',
          ...arg.data
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response.json();
    }
  );

  const approve = (approvalId, comments = '') => {
    return approveAction({ 
      approvalId,
      data: { comments }
    });
  };

  const reject = (approvalId, comments = '', rejectionReason = '') => {
    return rejectAction({ 
      approvalId,
      data: { comments, rejectionReason }
    });
  };

  const delegate = (approvalId, delegateToUserId, comments = '') => {
    return delegateAction({ 
      approvalId,
      data: { delegateToId: delegateToUserId, comments }
    });
  };

  const escalate = (approvalId, comments = '') => {
    return escalateAction({ 
      approvalId,
      data: { comments }
    });
  };

  return {
    approve,
    reject,
    delegate,
    escalate,
    isLoading: isApproving || isRejecting || isDelegating || isEscalating
  };
};

/**
 * Hook for bulk approval actions
 * @returns {object} { bulkApprove, bulkReject, isLoading, error }
 */
export const useBulkApprovalActions = () => {
  const { user } = useAuth(); // Get user from auth context
  
  const { trigger: bulkApproveAction, isMutating: isBulkApproving } = useSWRMutation(
    '/approval-requests/bulk-approve',
    async (url, { arg }) => {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.id && { 'user-id': user.id.toString() }) // Only include if user ID exists
        },
        body: JSON.stringify({
          action: 'approve',
          ...arg
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response.json();
    }
  );

  const { trigger: bulkRejectAction, isMutating: isBulkRejecting } = useSWRMutation(
    '/approval-requests/bulk-reject',
    async (url, { arg }) => {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.id && { 'user-id': user.id.toString() }) // Only include if user ID exists
        },
        body: JSON.stringify({
          action: 'reject',
          ...arg
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response.json();
    }
  );

  const bulkApprove = (approvalIds, comments = '') => {
    return bulkApproveAction({ 
      data: { approvalIds, comments }
    });
  };

  const bulkReject = (approvalIds, comments = '', rejectionReason = '') => {
    return bulkRejectAction({ 
      data: { approvalIds, comments, rejectionReason }
    });
  };

  return {
    bulkApprove,
    bulkReject,
    isLoading: isBulkApproving || isBulkRejecting
  };
};

// ============================================================================
// SPECIALIZED HOOKS - For specific approval workflows
// ============================================================================

/**
 * Hook for entity-specific approval requests
 * @param {string} entityType - Type of entity (document, capa, etc.)
 * @param {number} entityId - ID of the entity
 * @returns {object} { data, isLoading, isError, mutate }
 */
export const useEntityApprovals = (entityType, entityId) => {
  const url = entityType && entityId ? `/approval-requests/entity/${entityType}/${entityId}` : null;
  
  // console.log('useEntityApprovals - Fetching approvals:', {
  //   entityType,
  //   entityId,
  //   url
  // });
  
  const { data, isLoading, isError, mutate } = useFlexibleList(url);

  // console.log('useEntityApprovals - API response:', {
  //   data,
  //   isLoading,
  //   isError,
  //   dataLength: data?.length
  // });

  return {
    approvals: data || [],
    isLoading,
    isError,
    mutate
  };
};

/**
 * Hook for approval templates
 * @returns {object} { data, isLoading, isError, mutate }
 */
export const useApprovalTemplates = () => {
  const { data, isLoading, isError, mutate } = useFlexibleList('/approval-templates');

  return {
    templates: data || [],
    isLoading,
    isError,
    mutate
  };
};

/**
 * Hook for approval delegations
 * @returns {object} { data, isLoading, isError, mutate }
 */
export const useApprovalDelegations = () => {
  const { data, isLoading, isError, mutate } = useFlexibleList('/approval-delegations');

  return {
    delegations: data || [],
    isLoading,
    isError,
    mutate
  };
};

/**
 * Hook to send reminder emails for an approval request
 * @param {number} approvalId - Approval request ID
 * @returns {object} { trigger, isMutating, error }
 */
export const useSendReminder = (approvalId) => {
  const { trigger, isMutating, error } = useSWRMutation(
    approvalId ? `/approval-requests/${approvalId}/send-reminder` : null,
    async (url) => {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Failed to send reminder: ${response.statusText}`);
      }

      return response.json();
    }
  );

  return {
    sendReminder: trigger,
    isSending: isMutating,
    error,
  };
};

/**
 * Hook to fetch email notifications for an approval request
 * @param {number} approvalId - Approval request ID
 * @returns {object} { notifications, isLoading, isError, mutate }
 */
export const useEmailNotifications = (approvalId) => {
  const { data, isLoading, isError, mutate } = useFlexibleList(
    approvalId ? `/approval-requests/${approvalId}/email-notifications` : null
  );

  return {
    notifications: data || [],
    isLoading,
    isError,
    mutate,
  };
};

/**
 * Hook to resend a failed email notification
 * @param {number} approvalId - Approval request ID
 * @param {number} notificationId - Email notification ID
 * @returns {object} { trigger, isMutating, error }
 */
export const useResendEmail = (approvalId, notificationId) => {
  const { trigger, isMutating, error } = useSWRMutation(
    approvalId && notificationId
      ? `/approval-requests/${approvalId}/email-notifications/${notificationId}/resend`
      : null,
    async (url) => {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Failed to resend email: ${response.statusText}`);
      }

      return response.json();
    }
  );

  return {
    resendEmail: trigger,
    isResending: isMutating,
    error,
  };
};

const approvalHooks = {
  useApprovalRequests,
  useMyApprovals,
  useMyRequests,
  useApprovalRequest,
  useOverdueApprovals,
  useApprovalAnalytics,
  useCreateApprovalRequest,
  useUpdateApprovalRequest,
  useDeleteApprovalRequest,
  useCancelApprovalRequest,
  useApprovalActions,
  useBulkApprovalActions,
  useEntityApprovals,
  useApprovalTemplates,
  useApprovalDelegations,
  useSendReminder,
  useEmailNotifications,
  useResendEmail,
};

export default approvalHooks;